import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "requirements", "length", "uppercase", "number", "special", "submit"]
  static values = { optional: { type: Boolean, default: false }, alwaysShow: { type: Boolean, default: false } }

  connect() {
    // Initial validation on connect
    this.validate()
  }

  validate() {
    if (!this.hasInputTarget) return

    const password = this.inputTarget.value

    // Show/hide requirements based on password content
    if (this.hasRequirementsTarget) {
      if (password.length > 0 || this.alwaysShowValue) {
        this.showRequirements()
      } else {
        this.hideRequirements()
      }
    }

    const lengthValid = password.length >= 12
    const uppercaseValid = /[A-Z]/.test(password)
    const numberValid = /\d/.test(password)
    const specialValid = /[^a-zA-Z\d]/.test(password)

    if (this.hasLengthTarget) this.checkRequirement(this.lengthTarget, lengthValid)
    if (this.hasUppercaseTarget) this.checkRequirement(this.uppercaseTarget, uppercaseValid)
    if (this.hasNumberTarget) this.checkRequirement(this.numberTarget, numberValid)
    if (this.hasSpecialTarget) this.checkRequirement(this.specialTarget, specialValid)

    // Enable/disable submit button
    const allValid = lengthValid && uppercaseValid && numberValid && specialValid
    
    // If optional mode and password is empty, enable the button
    if (this.optionalValue && password.length === 0) {
      this.updateSubmitButton(true)
    } else {
      this.updateSubmitButton(allValid)
    }
  }

  showRequirements() {
    if (!this.hasRequirementsTarget) return
    
    const el = this.requirementsTarget
    if (el.classList.contains('hidden')) {
      el.classList.remove('hidden')
      el.style.maxHeight = '0'
      el.style.opacity = '0'
      el.style.overflow = 'hidden'
      el.style.transition = 'max-height 0.3s ease-out, opacity 0.3s ease-out'
      
      requestAnimationFrame(() => {
        el.style.maxHeight = el.scrollHeight + 'px'
        el.style.opacity = '1'
      })
    }
  }

  hideRequirements() {
    if (!this.hasRequirementsTarget) return
    
    const el = this.requirementsTarget
    if (!el.classList.contains('hidden')) {
      el.style.transition = 'max-height 0.3s ease-out, opacity 0.3s ease-out'
      el.style.maxHeight = '0'
      el.style.opacity = '0'
      
      setTimeout(() => {
        el.classList.add('hidden')
      }, 300)
    }
  }

  updateSubmitButton(enabled) {
    if (!this.hasSubmitTarget) return

    const button = this.submitTarget
    button.disabled = !enabled

    if (enabled) {
      button.classList.remove('opacity-50', 'cursor-not-allowed')
      button.classList.add('cursor-pointer')
    } else {
      button.classList.add('opacity-50', 'cursor-not-allowed')
      button.classList.remove('cursor-pointer')
    }
  }

  checkRequirement(element, passed) {
    const icon = element.querySelector('[data-icon]')

    if (passed) {
      element.classList.remove('text-gray-600')
      element.classList.add('text-green-700')
      if (icon) {
        icon.innerHTML = this.checkIcon
      }
    } else {
      element.classList.remove('text-green-700')
      element.classList.add('text-gray-600')
      if (icon) {
        icon.innerHTML = this.circleIcon
      }
    }
  }

  generate(event) {
    event.preventDefault()
    if (!this.hasInputTarget) return

    const password = this.generatePassword()
    this.inputTarget.value = password

    // Reveal the password so the user can see/copy it
    if (this.inputTarget.type === "password") {
      this.inputTarget.type = "text"
      const visibilityWrapper = this.inputTarget.closest("[data-controller='password-visibility']")
      if (visibilityWrapper) {
        const showIcon = visibilityWrapper.querySelector("[data-password-visibility-target='showIcon']")
        const hideIcon = visibilityWrapper.querySelector("[data-password-visibility-target='hideIcon']")
        if (showIcon) showIcon.classList.add("hidden")
        if (hideIcon) hideIcon.classList.remove("hidden")
      }
    }

    // Trigger validation and sync
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  generatePassword() {
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const lowercase = "abcdefghijklmnopqrstuvwxyz"
    const digits = "0123456789"
    const specials = "!@#$%^&*_+-="
    const all = uppercase + lowercase + digits + specials

    const required = [
      uppercase[Math.floor(Math.random() * uppercase.length)],
      lowercase[Math.floor(Math.random() * lowercase.length)],
      digits[Math.floor(Math.random() * digits.length)],
      specials[Math.floor(Math.random() * specials.length)]
    ]

    const remaining = Array.from({ length: 12 }, () =>
      all[Math.floor(Math.random() * all.length)]
    )

    const chars = [...required, ...remaining]
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]]
    }

    return chars.join("")
  }

  get checkIcon() {
    return `<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>`
  }

  get circleIcon() {
    return `<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
      <circle cx="12" cy="12" r="9" />
    </svg>`
  }
}
